import {
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalActionBar from 'components/modal-action-bar';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from '../validations';
import FormRole from './FormRole';
import { ListingEntry } from 'graphql/listings/queries';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  title: {
    padding: theme.spacing(2, 0),
    fontSize: '28px',
    overflowWrap: 'anywhere',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: { fontSize: theme.typography.h6.fontSize },
  },
  errorText: {
    color: theme.palette.error.main,
  },
  actions: {
    paddingBottom: `calc(${theme.spacing(1)} + env(safe-area-inset-bottom))`,
    '& .MuiButton-root': {
      '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
    },
  },
}));

interface ModalAddRoleProps {
  handleClose: () => void;
  open: boolean;
  listingMutations: ListingEntry[];
  listingQueries: ListingEntry[];
  handleCreateRole: (data: any) => void;
}

const ModalAddRole: FC<ModalAddRoleProps> = ({
  handleClose,
  open,
  listingMutations,
  listingQueries,
  handleCreateRole,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  // Full screen below sm (<600px); MUI v4 down('sm') would mean <960px.
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: { description: '', permissions: [] },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data) => {
    handleCreateRole(data);
    handleClose();
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      aria-labelledby="add-role-title"
    >
      <DialogContent>
        <DialogTitle id="add-role-title" className={classes.title}>{t('roles:addNewRoleTitle')}</DialogTitle>
        <FormRole
          errors={errors}
          control={control}
          listingMutations={listingMutations}
          listingQueries={listingQueries}
        />
      </DialogContent>
      <ModalActionBar
        onCancel={handleClose}
        submitType="button"
        submitText={t('common:save')}
        onSubmit={handleSubmit(onSubmit)}
        submitDisabled={!isValid}
      />
    </Dialog>
  );
};

export default ModalAddRole;
